/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Trigger syncs with dbt Cloud", description = "The dbt Cloud extension lets you schedule Hightouch syncs to run as soon as a dbt Cloud job completes. Relying on dbt Cloud for sync scheduling ensures that Hightouch performs syncs when fresh data is available.", lastUpdatedDate = new Date(1726099200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Trigger syncs with dbt Cloud",
        description: "The dbt Cloud extension lets you schedule Hightouch syncs to run as soon as a dbt Cloud job completes. Relying on dbt Cloud for sync scheduling ensures that Hightouch performs syncs when fresh data is available.",
        lastUpdatedDate: "Thu Sep 12 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Connect to dbt Cloud",
          "depth": 1,
          "id": "connect-to-dbt-cloud"
        }, {
          "value": "Configure your sync schedule",
          "depth": 1,
          "id": "configure-your-sync-schedule"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      a: "a",
      strong: "strong",
      code: "code",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Warning, {
        children: _jsx(_components.p, {
          children: "To schedule syncs via dbt Cloud, dbt requires you to have a paid plan."
        })
      }), "\n", _jsx(_components.p, {
        children: "The dbt Cloud extension lets you schedule Hightouch syncs to run as soon as a dbt Cloud job completes. Relying on dbt Cloud for sync scheduling ensures that Hightouch performs syncs when fresh data is available."
      }), "\n", _jsx(_components.p, {
        children: "Extension setup requires two steps:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Connecting your dbt account to Hightouch"
        }), "\n", _jsx(_components.li, {
          children: "Configuring your Hightouch sync to follow a dbt Cloud job"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-dbt-cloud",
        children: "Connect to dbt Cloud"
      }), "\n", _jsxs(_components.p, {
        children: ["Open the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/dbt-cloud/configuration",
          children: "dbt Cloud configuration page"
        }), " in your workspace and enter the ", _jsx(_components.strong, {
          children: "dbt Cloud URL"
        }), ". Depending on your setup, you need to insert:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["for regular dbt Cloud setups, a ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/cloud/about-cloud/access-regions-ip-addresses",
            children: "default access URL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["for accounts with cell-based account prefixes, you can find the custom ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/cloud/about-cloud/access-regions-ip-addresses#api-access-urls",
            children: "unique access URL"
          }), " in dbt on the ", _jsx(_components.strong, {
            children: "Account settings"
          }), " page (below the ", _jsx(_components.strong, {
            children: "Account information"
          }), " pane)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "These are the most commonly used default access URLs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "​­­https://cloud.getdbt.com​"
          }), ", for the US"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "­https://emea.getdbt.com​"
          }), ", for EMEA"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Then navigate to dbt Cloud and generate a ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/docs/dbt-cloud-apis/user-tokens",
          children: "personal access token (PAT)"
        }), " or a ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/docs/dbt-cloud-apis/service-tokens",
          children: "service account token"
        }), ". In Hightouch, enter your token in the ", _jsx(_components.strong, {
          children: "dbt Cloud API token"
        }), " field in the ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab of the dbt Cloud extension. Click ", _jsx(_components.strong, {
          children: "Connect"
        }), " to complete your connection configuration."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["A ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/dbt-cloud-apis/service-tokens#team-plans-using-service-account-tokens",
            children: "read-only"
          }), " service account token is sufficient for this integration."]
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you want to update your dbt Cloud API token in Hightouch, you can disconnect the extension and reconnect it using the new API token. This won't reset the schedule configuration of syncs that use the ", _jsx(_components.a, {
            href: "#configure-your-sync-schedule",
            children: "dbt Cloud scheduling"
          }), " method."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-sync-schedule",
        children: "Configure your sync schedule"
      }), "\n", _jsxs(_components.p, {
        children: ["Now, when creating or modifying a sync you can select ", _jsx(_components.strong, {
          children: "dbt Cloud"
        }), " as a schedule type."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go the ", _jsx(_components.strong, {
            children: "Schedule"
          }), " tab of overview page of the sync you want to schedule. Select ", _jsx(_components.strong, {
            children: "dbt Cloud"
          }), " as the schedule type."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the appropriate dbt ", _jsx(_components.strong, {
            children: "Account"
          }), " and ", _jsx(_components.strong, {
            children: "Job"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-configure.png",
          alt: "dbt Cloud schedule configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Your sync is now scheduled to run whenever your chosen dbt Cloud jobs complete."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The sync won't be triggered if your dbt Cloud job fails."
        })
      })]
    });
  }
}
export default MDXContent;
